import { ReactElement } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { Link } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import {
	isLocalizableHref,
	useLocalizedHref,
} from '@/libraries/web-framework/src/localization/use-localized-href';
import { ProductKeyTypes } from '@/libraries/wpl/foundations/src/data-event-props';

import { Pill } from '../product-subnav';

export type CardIcon = { normal: ReactElement; hover?: ReactElement };
export type TitleCardProps = {
	content: string;
	href: string;
	icon?: CardIcon;
	title: string;
	productKey?: ProductKeyTypes;
	isNew?: boolean;
};

const cardStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '11.5rem',
	[media.below.md]: {
		width: '100%',
		padding: `${token('space.200')} 0`,
	},

	':hover': {
		// NOTE: There isn't a way to style the hover state of a child element based on the hover state of the parent in CSS
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.default-icon': {
			display: 'none',
		},

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'.hover-icon': {
			display: 'block',
		},
	},
});

const hoverIconStyles = css({
	display: 'none',
});

const CardTitleCardContentStyle = css({
	fontSize: '11.5px',
	lineHeight: 'normal',
	color: wacToken('color.neutral.light800'),
	margin: 0,
	fontWeight: '400',
	letterSpacing: 0,
});

const CardTitleAnchorLinkStyle = css({
	alignItems: 'center',
	color: wacToken('color.neutral.light800'),
	display: 'block',
	'&:visited': {
		color: 'inherit',
	},
	'&:hover, &:focus': {
		color: wacToken('color.primary.blue'),
	},
	// NOTE:
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	svg: {
		transition: 'transform 0.25s ease-in-out',
	},
});

const subNavTitleStyles = css({
	lineHeight: 'normal',
	margin: 0,
	display: 'flex',
	alignItems: 'center',
	flexWrap: 'nowrap',
	gap: '6px',
	color: wacToken('color.primary.black'),
	fontWeight: 700,
	'&:visited': {
		color: 'inherit',
	},
	'&:hover, &:focus': {
		color: wacToken('color.primary.blue'),
	},
	fontSize: '1rem',
	letterSpacing: '0.03em',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	[`> div`]: {
		display: 'inline-flex',
		flexShrink: 0,
		fontSize: '10px',
		verticalAlign: 'middle',
		lineHeight: 1.6,
		fontWeight: 700,
		textTransform: 'uppercase',
	},
});

const iconContainerStyles = css({
	marginBlockEnd: '0.4rem',
});

const iconStyles = css({
	display: 'block',
	alignItems: 'end',
	height: '36px',
	width: '36px',
});

export const TitleCard = ({
	title,
	icon,
	content,
	href,
	productKey = 'home',
	isNew,
}: TitleCardProps) => {
	return (
		<div css={cardStyles}>
			<a
				className="title-card-link"
				href={href}
				css={CardTitleAnchorLinkStyle}
				target={isLocalizableHref(href) ? '' : '_blank'}
				rel={isLocalizableHref(href) ? '' : 'noreferrer'}
				aria-label={`${title}: ${content}`}
				data-event="clicked"
				data-event-component="linkButton"
				data-event-container="globalNavSubnav"
				data-product-key={productKey}
			>
				{!!icon && (
					<div css={iconContainerStyles}>
						<div css={iconStyles} className="default-icon">
							{icon.normal}
						</div>
						<div css={[iconStyles, hoverIconStyles]} className="hover-icon">
							{icon.hover}
						</div>
					</div>
				)}

				<div className="titleCard-title" css={subNavTitleStyles}>
					{title}
					{isNew && <Pill>New</Pill>}
				</div>
				<div className="titleCard-content" css={CardTitleCardContentStyle}>
					{content}
				</div>
			</a>
		</div>
	);
};

type LinkCardProps = {
	href: string;
	text: string;
	productKey?: ProductKeyTypes;
};

const linkCardStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	border: `1px solid wacToken("color.neutral.light300")`,
	borderRadius: '1rem',

	// NOTE: This is overwriting styles on a React component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	a: {
		color: wacToken('color.primary.black'),
		fontSize: '0.875rem',

		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'svg > path': {
			fill: wacToken('color.primary.black'),
		},
	},
});

export const LinkCard = ({ href, text, productKey = 'home' }: LinkCardProps) => {
	const localizedHref = useLocalizedHref(href);

	return (
		<div css={linkCardStyles}>
			{/* eslint-disable-next-line @atlassian/a11y/anchor-has-content, @atlassian/a11y/link-name */}
			<Link
				href={localizedHref}
				text={text}
				ariaLabel={text}
				type="primary"
				dataEventProps={{
					'data-event': 'clicked',
					'data-event-component': 'linkButton',
					'data-event-container': 'globalNavSubnav',
					'data-product-key': productKey,
				}}
			/>
		</div>
	);
};
